import type {VehicleSafelistingsAccountability} from "@/src/modules/vehicles/types";

import type {SafelistingAvailability} from "../types/safelisting-availability";
import type {LimitBannerDescriptor, LimitBannerVariant} from "./compute-limit-banner";

function permitsAvailabilityTitle(remaining: number, totalAllowed: number) {
  return `You have ${remaining} of ${totalAllowed} permits available.`;
}

function variantForReasonCode(
  reasonCode: SafelistingAvailability["reason_code"],
): LimitBannerVariant {
  if (reasonCode === "advance_window") return "warning";
  return "destructive";
}

/**
 * Maps `GET /safelistings/availability` to the shared limit-banner descriptor.
 * Used on create when the user has selected anchor dates — not for today baseline.
 */
export function computeAvailabilityBanner(
  availability: SafelistingAvailability | null,
  accountability: VehicleSafelistingsAccountability | null,
  permitDays: number,
): LimitBannerDescriptor | null {
  if (!availability || !accountability) return null;

  const {total_allowed, used, rolling_days, period_type} = accountability;
  if (total_allowed == null || used == null) return null;

  const remaining =
    availability.remaining ?? accountability.remaining ?? null;
  if (remaining == null) return null;

  const safePermitDays = Math.max(0, permitDays);
  const base = {
    totalAllowed: total_allowed,
    used,
    remaining,
    totalDays: safePermitDays,
    nextReset: availability.next_reset ?? accountability.next_reset,
    rollingDays: rolling_days,
    periodType: period_type,
  };

  if (availability.available) {
    return {
      ...base,
      variant: "info",
      title: permitsAvailabilityTitle(remaining, total_allowed),
      shortfall: null,
      remainingAfterSubmit:
        safePermitDays > 0 ? Math.max(0, remaining - safePermitDays) : remaining,
      availabilityReason: null,
    };
  }

  if (availability.reason_code === "no_quota") {
    return null;
  }

  const variant = variantForReasonCode(availability.reason_code);
  const title =
    availability.reason?.trim() ||
    (variant === "warning"
      ? permitsAvailabilityTitle(remaining, total_allowed)
      : `No permits available (${remaining} of ${total_allowed} left)`);

  const shortfall =
    availability.reason_code === "advance_window"
      ? null
      : safePermitDays > 0
        ? safePermitDays
        : null;

  return {
    ...base,
    variant,
    title,
    shortfall,
    remainingAfterSubmit: variant === "info" ? remaining : null,
    availabilityReason: availability.reason,
  };
}
